import React from 'react';
import { embedUrlFor, isPlayable, staffTrackLabel } from '../../../../services/studentTracksModel';
import StageButton from '../stage/StageButton';
import { defaultCut, jobChip, stripSubject } from './trackStripModel';
import './TrackStrip.css';

/**
 * The track strip (Lesson Dashboard pass 3, batch E, 2026-09-28): the song
 * tab's sibling of the warm-up conducting strip, in the same slot, pinned
 * above the notes and never scrolling with them.
 *
 * ONE LINE SINCE 2026-10-29. Now Playing (songpanel/NowPlayingBar.js) put a
 * full player under the lyrics, and Anthony: "would it be safe to remove the
 * player from the top of the page now? I feel like its just taking up space."
 * Not removed: under 1200 the lyrics are a sheet, so this is the only player
 * on screen while notes are written, and it is the one that can stop ANOTHER
 * song's track. So it is the mini player: Play / Pause, the song and file
 * with the time, the job chip, **Tracks** and Second screen. The key, the
 * scrub bar, loop and vocals live in Now Playing; Adjust (section times),
 * Trim / Save as a cut and the song's key moved to the Tracks panel's
 * "Playing track" section; switching cuts is the Tracks panel's list.
 *
 * It FOLLOWS WHAT IS PLAYING (E5, Anthony: "the controls … should stay
 * visible"): switch tabs mid-play and it keeps the playing track, labelled
 * with that track's song, so what is sounding can be seen and stopped from
 * anywhere. A file with no Storage copy opens Drive's player in a second row.
 *
 * States (E3): no cuts = one row, "No tracks for this song yet." + Add a
 * track. Plain <button>s throughout, never `wa-*` (a boolean prop can stick
 * on under React 18); every one declares its own height (native.css).
 */
const TrackStrip = ({ song, files = [], player, trackDocs = [], jobs = [], anyMacOn = true, dismissed = new Set(), nowMs = null, onOpenTracks = null, stage = null }) => {
	if (!song || !player) return null;
	const now = nowMs || Date.now();
	const tabName = song.metadata?.title || song.name || '';
	const subject = stripSubject({ current: player.current, tabFiles: files, trackDocs, tabSongName: tabName });
	const chip = jobChip(jobs, { nowMs: now, anyMacOn, dismissed });
	const pills = files.filter((f) => isPlayable(f) || f.driveUrl);

	// Nothing on this tab and nothing playing: one quiet row (E3).
	if (!pills.length && !subject.file) {
		return (
			<section className="ld-tstrip ld-tstrip--empty" aria-label="Tracks">
				<p className="ld-tstrip__empty">No tracks for this song yet.</p>
				{chip && <JobChip chip={chip} onOpen={onOpenTracks} />}
				{onOpenTracks && (
					<button type="button" className="ld-tstrip__pill ld-tstrip__pill--add" data-guide="strip-add-track" onClick={() => onOpenTracks('add')}>
						<i className="fa-sharp fa-solid fa-plus" aria-hidden="true" />
						Add a track
					</button>
				)}
				<StageButton {...(stage || {})} />
			</section>
		);
	}

	const current = player.current;
	const loaded = subject.file;
	const cut = loaded || defaultCut(files, song);
	const isEmbed = Boolean(loaded && current && current.engine === 'embed');
	const playing = Boolean(loaded && current.engine === 'audio' && player.isPlaying);
	const label = cut ? staffTrackLabel(cut) : 'Track';
	// The song is ALWAYS named, then which file (2026-09-29, Anthony: "we need
	// the title of the song to appear on the player… it's unclear what's
	// playing up top if you don't know that you've selected the song itself").
	// A cut's Length word follows, since a song can now carry several cuts.
	const songTitle = subject.otherSong ? subject.songName : tabName;
	const lengthWord = cut && cut.length && cut.length !== 'Full' ? cut.length : null;
	const fileWords = [label, lengthWord].filter(Boolean).join(' · ');
	const where = songTitle ? `${songTitle} · ${fileWords}` : fileWords;
	const hasDuration = Boolean(loaded && !isEmbed && player.duration > 0);

	return (
		<section className={`ld-tstrip${subject.otherSong ? ' is-other-song' : ''}`} aria-label="Tracks">
			<div className="ld-tstrip__rowa">
				<button
					type="button"
					className={`ld-tstrip__play${playing ? ' is-playing' : ''}`}
					aria-label={playing ? `Pause ${where}` : `Play ${where}`}
					disabled={!cut}
					onClick={() => cut && player.toggle(cut)}>
					<i className={`fa-sharp fa-solid ${playing ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" />
				</button>

				<p className="ld-tstrip__name" title={cut ? cut.name || where : ''}>
					{/* "Blackbird · Track · Cut 45s": the song first, in full, then
					    the file. E5: another tab's song is in the brand colour, so it
					    reads as "still sounding", not as this tab. */}
					{songTitle && <span className={`ld-tstrip__song${subject.otherSong ? ' ld-tstrip__other' : ''}`}>{songTitle}</span>}
					{songTitle && <span className="ld-tstrip__sep"> · </span>}
					<span className="ld-tstrip__file">{fileWords}</span>
					{hasDuration && (
						<span className="ld-tstrip__time">
							{player.formatTime(player.currentTime)} / {player.formatTime(player.duration)}
						</span>
					)}
				</p>

				{chip && <JobChip chip={chip} onOpen={onOpenTracks} />}
				{onOpenTracks && (
					<button type="button" className="ld-tstrip__pill ld-tstrip__pill--all" data-guide="strip-all-tracks" onClick={() => onOpenTracks()}>
						<i className="fa-sharp fa-solid fa-list-music" aria-hidden="true" />
						Tracks
					</button>
				)}
				<StageButton {...(stage || {})} />
			</div>

			{/* A file with no Storage copy plays in Drive's own player, the one
			    place it can (the page player's embed engine); it needs room, so it takes a row. */}
			{isEmbed && (
				<div className="ld-tstrip__embedwrap">
					{embedUrlFor(current.file) ? (
						<iframe className="ld-tstrip__embed" src={embedUrlFor(current.file)} title={`Play ${label}`} allow="autoplay" allowFullScreen={false} />
					) : (
						<p className="ld-tstrip__msg">This track has no Drive link yet.</p>
					)}
					{current.file.driveUrl && (
						<a className="ld-tstrip__drive" href={current.file.driveUrl} target="_blank" rel="noopener noreferrer">
							<i className="fa-sharp fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
							Open in Drive
						</a>
					)}
				</div>
			)}

			{player.error && loaded && (
				<div className="ld-dismissable-error ld-tstrip__error" role="alert">
					<p>{player.error}</p>
					{player.clearError && (
						<button type="button" className="ld-dismiss" onClick={player.clearError}>
							Dismiss
						</button>
					)}
				</div>
			)}
		</section>
	);
};

/** The one-line job summary; opens Tracks mode, where every job has its line. */
const JobChip = ({ chip, onOpen }) => {
	const icon = chip.tone === 'failed' ? 'fa-triangle-exclamation' : chip.tone === 'done' ? 'fa-circle-check' : 'fa-spinner-third fa-spin';
	return (
		<button
			type="button"
			className={`ld-tstrip__chip ld-tstrip__chip--${chip.tone}`}
			onClick={() => onOpen && onOpen()}
			data-testid="track-job-chip">
			<i className={`fa-sharp fa-solid ${icon}`} aria-hidden="true" />
			<span className="ld-tstrip__chiptext">{chip.text}</span>
		</button>
	);
};

export default TrackStrip;
